import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { waLink } from "../lib/scroll";
import { BAKES } from "../lib/bakes";

export default function OrderBuilder() {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState({});

  const total = Object.values(qty).reduce((a, b) => a + b, 0);
  const priceTotal = BAKES.reduce((sum, b) => sum + (qty[b.id] || 0) * b.price, 0);
  const lines = BAKES.filter((b) => qty[b.id]).map(
    (b) => `${qty[b.id]}× ${b.name} — ₹${(qty[b.id] || 0) * b.price}`
  );
  const message = `Hi Zoni's Bakehouse! I'd like to order:\n${lines.join("\n")}\nTotal: ₹${priceTotal}`;

  const openDrawer = () => {
    setOpen(true);
    if (window.__lenis) window.__lenis.stop();
    document.body.style.overflow = "hidden";
  };
  const close = () => {
    setOpen(false);
    if (window.__lenis) window.__lenis.start();
    document.body.style.overflow = "";
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const bump = (id, d) =>
    setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + d) }));

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            onClick={openDrawer}
            data-testid="order-builder-fab"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35 }}
            className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 z-50 bg-rust text-eggshell pl-5 pr-6 py-4 rounded-full flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.18em] shadow-[6px_6px_0_rgba(28,21,18,0.25)] hover:bg-ink transition-colors duration-300"
          >
            <ShoppingBag size={15} />
            Build your box
            {total > 0 && (
              <span className="bg-eggshell text-rust rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center text-[10px] font-medium" data-testid="order-builder-count">
                {total}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              onClick={close}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm"
              aria-hidden="true"
            />
            <motion.aside
              data-testid="order-builder-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }}
              className="fixed right-0 top-0 h-full w-full sm:w-[430px] z-[70] bg-eggshell border-l-[3px] border-ink flex flex-col"
            >
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b-[1.5px] border-ink">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-rust">Order</p>
                  <p className="font-display font-bold text-2xl tracking-tight">Build your box</p>
                </div>
                <button
                  onClick={close}
                  data-testid="order-builder-close"
                  aria-label="Close order builder"
                  className="w-10 h-10 rounded-full border-[1.5px] border-ink flex items-center justify-center hover:bg-ink hover:text-eggshell transition-colors duration-300"
                >
                  <X size={17} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-4">
                {BAKES.map((b) => (
                  <div
                    key={b.id}
                    data-testid="order-builder-item"
                    className="flex items-center gap-4 py-4 border-b border-line last:border-0"
                  >
                    <img
                      src={b.img}
                      alt={b.name}
                      loading="lazy"
                      className="w-14 h-14 rounded-xl object-cover border-2 border-ink/80"
                    />
                    <div className="flex-1">
                      <p className="font-display font-bold text-sm sm:text-base leading-snug">
                        {b.name}
                      </p>
                      <p className="font-mono text-[11px] text-rust mt-1" data-testid="order-item-price">
                        ₹{b.price}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={() => bump(b.id, -1)}
                        data-testid={`order-stepper-minus-${b.id}`}
                        aria-label={`Remove one ${b.name}`}
                        className="w-8 h-8 rounded-full border-[1.5px] border-ink flex items-center justify-center hover:bg-ink hover:text-eggshell transition-colors duration-200 disabled:opacity-30"
                        disabled={!qty[b.id]}
                      >
                        <Minus size={13} />
                      </button>
                      <span
                        data-testid={`order-qty-${b.id}`}
                        className="w-5 text-center font-mono text-sm"
                      >
                        {qty[b.id] || 0}
                      </span>
                      <button
                        onClick={() => bump(b.id, 1)}
                        data-testid={`order-stepper-plus-${b.id}`}
                        aria-label={`Add one ${b.name}`}
                        className="w-8 h-8 rounded-full border-[1.5px] border-ink flex items-center justify-center hover:bg-ink hover:text-eggshell transition-colors duration-200"
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-6 sm:px-8 py-5 border-t-[1.5px] border-ink bg-cream/60">
                {total === 0 ? (
                  <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mutedbrown py-3" data-testid="order-builder-empty-hint">
                    Add something you&apos;re craving
                  </p>
                ) : (
                  <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mutedbrown py-2" data-testid="order-builder-summary">
                    {total} item{total > 1 ? "s" : ""} · ₹{priceTotal} in your box
                  </p>
                )}
                <a
                  href={lines.length ? waLink(message) : undefined}
                  onClick={(e) => !lines.length && e.preventDefault()}
                  data-testid="order-builder-send"
                  aria-disabled={!lines.length}
                  className={`block text-center rounded-full px-6 py-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    lines.length
                      ? "bg-rust text-eggshell hover:bg-ink"
                      : "bg-ink/20 text-ink/50 cursor-not-allowed"
                  }`}
                >
                  Send order on WhatsApp
                </a>
                <p className="text-center text-[11px] text-mutedbrown mt-3">
                  We&apos;ll confirm timing and details on WhatsApp.
                </p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
